import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { useLocation } from "react-router-dom";

// @material-ui/core components
import { Card, CardContent, CardHeader } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import { makeStyles } from "@material-ui/core/styles";
// @material-ui/icons
import SearchIcon from "@material-ui/icons/Search";
// components
import GenericButton from "components/Buttons/GenericButton";
//import custom components
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import FormSelect from "components/FormFields/FormSelect";
import useFilterSync from "hooks/useFilterSync";
import { getFilteredAvailableOpportunitiesMap } from "mappers/GetFilteredOpportunitiesMap";
import { getDataSetByNameAction } from "redux/actions/datasetsAction";
import { getFilteredAvailableOpportunitiesAction } from "redux/actions/opportunitiesAction";
import { DATASET, OPPORTUNITY_TYPE_KEY, PAGINATION } from "utils/const";
import { getDatasetStateByName } from "utils/datasets";
import { getSelectOptionsElementByKeyValue } from "utils/formSelect";

const useStylesGeneric = makeStyles(componentStylesGeneric);

function FilterSection(props) {
  const classes = useStylesGeneric();
  const location = useLocation();
  const [opportunityType, setOpportunityType] = useState();
  const submittedFilterRef = useRef(null);
  const initializedRef = useRef(false);
  const readyRef = useRef(false);

  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    control,
    formState: { errors },
  } = useFormContext();

  const pageIndex = watch("pageIndex");
  const pageSize = watch("pageSize");

  const provincesOptions = getDatasetStateByName(props, DATASET.PROVINCES);
  const opportunityTypeOptions = getDatasetStateByName(props, DATASET.OPPORTUNITY_TYPE);
  const professionalCategoryOptions = getDatasetStateByName(props, DATASET.PROFESSIONAL_CATEGORY);
  const contractTypeOptions = getDatasetStateByName(props, DATASET.CONTRACT_TYPE);
  const timeTableTypeOptions = getDatasetStateByName(props, DATASET.TIMETABLE_TYPE);

  const findOptionByCode = (options, code) => {
    if (!options || options.length === 0 || code === null || code === undefined || code === "") return null;
    const codeStr = String(code);
    return options.find((o) => String(o?.code) === codeStr) || null;
  };

  const buildPayload = (values, override) => {
    const effectivePageIndex = override?.pageIndex ?? values?.pageIndex ?? PAGINATION.FIRST_PAGE;
    const effectivePageSize = override?.pageSize ?? values?.pageSize ?? PAGINATION.PAGE_SIZE_AVAILABLE_OPPORTUNITIES;

    return {
      ...getFilteredAvailableOpportunitiesMap(values),
      pageIndex: effectivePageIndex,
      pageSize: effectivePageSize,
    };
  };

  const fieldSerializers = useMemo(
    () => ({
      opportunityType: {
        toUrl: (opt) => opt?.code ?? null,
        fromUrl: (code) => findOptionByCode(opportunityTypeOptions, code),
      },
      province: {
        toUrl: (opt) => opt?.code ?? null,
        fromUrl: (code) => findOptionByCode(provincesOptions, code),
      },
      opportunityProfessionalCategory: {
        toUrl: (opt) => opt?.code ?? null,
        fromUrl: (code) => findOptionByCode(professionalCategoryOptions, code),
      },
      contractType: {
        toUrl: (opt) => opt?.code ?? null,
        fromUrl: (code) => findOptionByCode(contractTypeOptions, code),
      },
      timeTableType: {
        toUrl: (opt) => opt?.code ?? null,
        fromUrl: (code) => findOptionByCode(timeTableTypeOptions, code),
      },
    }),
    [
      provincesOptions,
      opportunityTypeOptions,
      professionalCategoryOptions,
      contractTypeOptions,
      timeTableTypeOptions,
    ]
  );

  const filterSync = useFilterSync({
    defaultValues: props.defaultFormValues || {
      opportunityType: null,
      province: null,
      opportunityProfessionalCategory: null,
      contractType: null,
      internshipType: null,
      timeTableType: null,
      companyName: "",
      searchText: "",
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE_AVAILABLE_OPPORTUNITIES,
    },
    syncPagination: true,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
      defaultPageSize: props.defaultFormValues?.pageSize ?? PAGINATION.PAGE_SIZE_AVAILABLE_OPPORTUNITIES,
    },
    fieldSerializers,
    onFilterChange: (values) => {
      if (!readyRef.current) return;

      // Se filtros mudaram por URL (back/forward), garante que estamos vendo a lista.
      props.setShowRecommendedOpportunities?.(false);
      props.setFiltersReady?.(true);

      const next = buildPayload(values);
      submittedFilterRef.current = next;
      props.getFilteredAvailableOpportunities(next);
    },
  });

  useEffect(() => {
    if (props.datasetsState[DATASET.PROVINCES] === undefined || props.datasetsState[DATASET.PROVINCES]?.length === 0) {
      props.getDataSetByName(DATASET.PROVINCES);
    }
    if (
      props.datasetsState[DATASET.OPPORTUNITY_TYPE] === undefined ||
      props.datasetsState[DATASET.OPPORTUNITY_TYPE]?.length === 0
    ) {
      props.getDataSetByName(DATASET.OPPORTUNITY_TYPE);
    } else {
      setOpportunitytypeFormSelect();
    }
    if (
      props.datasetsState[DATASET.PROFESSIONAL_CATEGORY] === undefined ||
      props.datasetsState[DATASET.PROFESSIONAL_CATEGORY]?.length === 0
    ) {
      props.getDataSetByName(DATASET.PROFESSIONAL_CATEGORY);
    }
    if (
      props.datasetsState[DATASET.CONTRACT_TYPE] === undefined ||
      props.datasetsState[DATASET.CONTRACT_TYPE]?.length === 0
    ) {
      props.getDataSetByName(DATASET.CONTRACT_TYPE);
    }
    if (
      props.datasetsState[DATASET.TIMETABLE_TYPE] === undefined ||
      props.datasetsState[DATASET.TIMETABLE_TYPE]?.length === 0
    ) {
      props.getDataSetByName(DATASET.TIMETABLE_TYPE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inicialização: se usuário entra com query params, espera datasets e aplica URL no form antes de buscar.
  useEffect(() => {
    if (initializedRef.current) return;

    const hasUrlParams = (location.search || "").length > 1;
    const requiresOptions = hasUrlParams;
    const optionsReady =
      !requiresOptions ||
      (provincesOptions.length > 0 &&
        opportunityTypeOptions.length > 0 &&
        professionalCategoryOptions.length > 0 &&
        contractTypeOptions.length > 0 &&
        timeTableTypeOptions.length > 0);

    if (requiresOptions && !optionsReady) return;

    readyRef.current = false;

    if (hasUrlParams) {
      props.setShowRecommendedOpportunities?.(false);
      filterSync.syncFiltersFromUrl();
    }

    readyRef.current = true;
    props.setFiltersReady?.(true);

    if (hasUrlParams) {
      const values = getValues();
      const next = buildPayload(values);
      submittedFilterRef.current = next;
      props.getFilteredAvailableOpportunities(next);
    }

    initializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.search,
    provincesOptions.length,
    opportunityTypeOptions.length,
    professionalCategoryOptions.length,
    contractTypeOptions.length,
    timeTableTypeOptions.length,
  ]);

  useEffect(() => {
    if (props.filtersReady === false) return;
    if (!props.showRecommendedOpportunities) refreshResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, props.filtersReady]);

  useEffect(() => {
    if (!props.showRecommendedOpportunities && props.preLoadOpportunities) refreshResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.preLoadOpportunities]);

  useEffect(() => {
    setOpportunitytypeFormSelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.datasetsState[DATASET.OPPORTUNITY_TYPE]]);

  const setOpportunitytypeFormSelect = () => {
    if (props.match?.params?.oppType === OPPORTUNITY_TYPE_KEY.EMPREGO_URL) {
      let auxData = [];
      auxData["opportunityTypeKey"] = OPPORTUNITY_TYPE_KEY.EMPREGO;
      setOpportunityType(auxData);
      setValue(
        "opportunityType",
        getSelectOptionsElementByKeyValue(props.datasetsState[DATASET.OPPORTUNITY_TYPE], OPPORTUNITY_TYPE_KEY.EMPREGO)
      );

      handleSubmit(onSubmit)();
    } else if (props.match?.params?.oppType === OPPORTUNITY_TYPE_KEY.ESTAGIO_PROFISSIONAL_URL) {
      let auxData = [];
      auxData["opportunityTypeKey"] = OPPORTUNITY_TYPE_KEY.ESTAGIO_PROFISSIONAL;
      setOpportunityType(auxData);
      setValue(
        "opportunityType",
        getSelectOptionsElementByKeyValue(
          props.datasetsState[DATASET.OPPORTUNITY_TYPE],
          OPPORTUNITY_TYPE_KEY.ESTAGIO_PROFISSIONAL
        )
      );

      handleSubmit(onSubmit)();
    }
  };

  const cleanFilter = () => {
    const nextFormValues = {
      opportunityType: null,
      province: null,
      opportunityProfessionalCategory: null,
      contractType: null,
      internshipType: null,
      timeTableType: null,
      companyName: "",
      searchText: "",
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE_AVAILABLE_OPPORTUNITIES,
    };

    reset(nextFormValues);

    filterSync.handleFilterReset();

    const next = {
      ...getFilteredAvailableOpportunitiesMap(nextFormValues),
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE_AVAILABLE_OPPORTUNITIES,
    };

    submittedFilterRef.current = next;
    props.setShowRecommendedOpportunities(false);

    if ((getValues("pageIndex") ?? PAGINATION.FIRST_PAGE) === PAGINATION.FIRST_PAGE) {
      props.getFilteredAvailableOpportunities(next);
    } else {
      setValue("pageIndex", PAGINATION.FIRST_PAGE);
    }
  };

  const getContractTypeFieldByOpportunityType = (selectedOpportunityType) => {
    switch (selectedOpportunityType) {
      case OPPORTUNITY_TYPE_KEY.EMPREGO:
        return (
          <Grid item xs={12} sm={12} md={4}>
            <FormGroup>
              <FormLabel>Tipo de Contrato</FormLabel>
              <FormSelect
                control={control}
                fieldName="contractType"
                selectOptions={getDatasetStateByName(props, DATASET.CONTRACT_TYPE)}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                classes={classes}
              />
            </FormGroup>
          </Grid>
        );
      default:
        return "";
    }
  };
  const controlOpportunityTypeRelatedFields = (selectedOpportunityType) => {
    if (selectedOpportunityType) {
      let auxData = [];
      auxData["opportunityTypeKey"] = selectedOpportunityType.keyValue;
      switch (selectedOpportunityType.keyValue) {
        case OPPORTUNITY_TYPE_KEY.EMPREGO:
          window.history.replaceState(null, "", `employment`);
          break;
        case OPPORTUNITY_TYPE_KEY.ESTAGIO_PROFISSIONAL:
          window.history.replaceState(null, "", `internship`);
          break;
        default:
          window.history.replaceState(null, "", ``);
      }
      setOpportunityType(auxData);
    } else {
      setOpportunityType(undefined);
    }
  };
  //Refresh Results
  const refreshResults = () => {
    if (props.filtersReady === false) return;

    const effectivePageIndex = pageIndex ?? PAGINATION.FIRST_PAGE;
    const effectivePageSize = pageSize ?? PAGINATION.PAGE_SIZE_AVAILABLE_OPPORTUNITIES;

    filterSync.updatePagination(effectivePageIndex, effectivePageSize);

    if (!submittedFilterRef.current) {
      const values = getValues();
      submittedFilterRef.current = {
        ...getFilteredAvailableOpportunitiesMap(values),
        pageIndex: effectivePageIndex,
        pageSize: effectivePageSize,
      };
    } else {
      submittedFilterRef.current = {
        ...submittedFilterRef.current,
        pageIndex: effectivePageIndex,
        pageSize: effectivePageSize,
      };
    }

    props.getFilteredAvailableOpportunities(submittedFilterRef.current);
  };

  const onSubmit = (data) => {
    props.setShowRecommendedOpportunities(false);
    const nextFormValues = {
      ...data,
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: data.pageSize ?? PAGINATION.PAGE_SIZE_AVAILABLE_OPPORTUNITIES,
    };

    // Submit sempre volta para a 1ª página e sincroniza URL
    if ((data.pageIndex ?? PAGINATION.FIRST_PAGE) !== PAGINATION.FIRST_PAGE) {
      setValue("pageIndex", PAGINATION.FIRST_PAGE);
    }

    filterSync.handleFilterSubmit(nextFormValues);

    const next = buildPayload(nextFormValues, {
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: nextFormValues.pageSize,
    });

    submittedFilterRef.current = next;

    if ((data.pageIndex ?? PAGINATION.FIRST_PAGE) === PAGINATION.FIRST_PAGE) {
      props.getFilteredAvailableOpportunities(next);
    } else {
      setValue("pageIndex", PAGINATION.FIRST_PAGE);
    }
  };
  return (
    <Card classes={{ root: classes.cardRoot }}>
      <CardHeader
        classes={{
          action: classes.cardHeaderAction,
          title: classes.cardHeader,
        }}
        title={"Pesquisa de Ofertas"}
      ></CardHeader>
      <CardContent>
        <Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Tipo de Oferta</FormLabel>
                  <FormSelect
                    control={control}
                    fieldName="opportunityType"
                    selectOptions={getDatasetStateByName(props, DATASET.OPPORTUNITY_TYPE)}
                    getValues={getValues}
                    setValue={setValue}
                    onOptionChange={controlOpportunityTypeRelatedFields}
                    errors={errors}
                    classes={classes}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Província</FormLabel>
                  <FormSelect
                    control={control}
                    fieldName="province"
                    selectOptions={getDatasetStateByName(props, DATASET.PROVINCES)}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                    classes={classes}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Área Profissional</FormLabel>
                  <FormSelect
                    control={control}
                    fieldName="opportunityProfessionalCategory"
                    selectOptions={getDatasetStateByName(props, DATASET.PROFESSIONAL_CATEGORY)}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                    classes={classes}
                  />
                </FormGroup>
              </Grid>
              {opportunityType?.opportunityTypeKey &&
                getContractTypeFieldByOpportunityType(opportunityType.opportunityTypeKey)}
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Horário</FormLabel>
                  <FormSelect
                    control={control}
                    fieldName="timeTableType"
                    selectOptions={getDatasetStateByName(props, DATASET.TIMETABLE_TYPE)}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                    classes={classes}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Pesquisa (título/descrição)</FormLabel>
                  <Controller
                    name="searchText"
                    control={control}
                    defaultValue=""
                    component={Box}
                    render={({ field: { onChange, name, value } }) => (
                      <OutlinedInput
                        name={name}
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        fullWidth
                        autoComplete="off"
                        type="text"
                        placeholder="Pesquisar"
                        endAdornment={
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        }
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormGroup>
                  <FormLabel>Empresa</FormLabel>
                  <Controller
                    name="companyName"
                    control={control}
                    defaultValue=""
                    component={Box}
                    render={({ field: { onChange, name, value } }) => (
                      <OutlinedInput
                        name={name}
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        fullWidth
                        autoComplete="off"
                        type="text"
                        placeholder="Pesquisar"
                        endAdornment={
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        }
                      />
                    )}
                  />
                </FormGroup>
              </Grid>

              {/* Botões de Pesquisa */}

              <Grid item xs={12} md={12} classes={{ root: classes.textAlignRight }}>
                <GenericButton
                  typeSubmit={false}
                  color="ghost"
                  onClick={() => {
                    cleanFilter();
                  }}
                >
                  Limpar Filtros
                </GenericButton>
                <GenericButton
                  typeSubmit={true}
                  color="primary"
                  loading={
                    props.opportunitiesState?.searchAvailableOpportunities_loading ||
                    props.opportunitiesState?.searchRecommendedOpportunities_loading
                  }
                >
                  Pesquisar
                </GenericButton>
              </Grid>
            </Grid>
          </form>
        </Box>
      </CardContent>
    </Card>
  );
}

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getFilteredAvailableOpportunities: (data) => dispatch(getFilteredAvailableOpportunitiesAction(data)),
  getDataSetByName: (data, parentKeyValue) => dispatch(getDataSetByNameAction(data, parentKeyValue)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(FilterSection));
